import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useNavigate, useLocation } from "react-router-dom";
import { Colors } from "../utils/colors";
import { API } from "../constant/apiConstant";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: "info",
    message: ""
  });

  const showAlert = (type, message, time = 5000) => {
    setAlert({
      show: true,
      type,
      message
    });

    setTimeout(() => {
      setAlert((prev) => ({ ...prev, show: false }));
    }, time);
  };

  const resetPassword = async () => {
    if (password !== confirmPassword) {
      showAlert("error", "Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API.BASE_URL}/api/admin/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await res.json();

      if (!data.status) {
        showAlert("error", data.message || "Unable to reset password");
        setLoading(false);
        return;
      }

      showAlert("success", "Password reset successful. Redirecting to login...", 2500);

      setTimeout(() => {
        navigate("/");
      }, 1800);
    } catch (error) {
      showAlert("error", "Unable to connect to server");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: Colors.secondary,
        p: 2
      }}
    >
      <Card sx={{ p: 4, width: "100%", maxWidth: 520, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent }}>
          Reset Password
        </Typography>

        {alert.show && (
          <Alert severity={alert.type} sx={{ mt: 2 }}>
            {alert.message}
          </Alert>
        )}

        <TextField label="Admin Email" fullWidth sx={{ mt: 2 }} value={email} disabled />

        <TextField
          label="New Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          sx={{ mt: 2 }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          fullWidth
          sx={{ mt: 2 }}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                  {showConfirmPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 3, backgroundColor: Colors.secondary, color: "white" }}
          onClick={resetPassword}
          disabled={password.length < 6 || password !== confirmPassword || loading}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Reset Password"}
        </Button>
      </Card>
    </Box>
  );
}
